import { useState, useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { formatError } from '../utils/errorHandler';

/**
 * Hook para fazer mutations (POST, PUT, DELETE) com tratamento de erros
 * 
 * @param {Function} mutationFn - Função que retorna uma Promise
 * @param {Object} options - Opções de configuração
 * @param {Function} options.onSuccess - Callback chamado em caso de sucesso
 * @param {Function} options.onError - Callback chamado em caso de erro
 * @param {boolean} options.showSuccess - Se deve mostrar notificação de sucesso (padrão: false)
 * @param {boolean} options.showError - Se deve mostrar notificação de erro (padrão: true)
 * @param {string} options.successMessage - Mensagem de sucesso customizada
 * @param {string} options.errorMessage - Mensagem de erro customizada
 * 
 * @returns {Object} { mutate, mutateAsync, data, error, loading, reset }
 */
export const useMutation = (mutationFn, options = {}) => {
  const {
    onSuccess,
    onError,
    showSuccess = false,
    showError = true,
    successMessage,
    errorMessage,
  } = options;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { showSuccess: showSuccessNotification, showError: showErrorNotification } = useNotification();

  const mutateAsync = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);

      const result = await mutationFn(...args);
      
      setData(result);
      setError(null);

      if (showSuccess) {
        showSuccessNotification(
          successMessage || 'Operação realizada com sucesso!',
          'Sucesso'
        );
      }

      if (onSuccess) {
        onSuccess(result, ...args);
      }

      return result;
    } catch (err) {
      const formattedError = formatError(err);
      setError(formattedError);

      if (showError) {
        showErrorNotification(
          errorMessage || formattedError.message,
          'Erro'
        );
      }

      if (onError) {
        onError(formattedError, ...args);
      }

      throw formattedError;
    } finally {
      setLoading(false);
    }
  }, [
    mutationFn,
    onSuccess,
    onError,
    showSuccess,
    showError,
    successMessage,
    errorMessage,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const mutate = useCallback((...args) => {
    mutateAsync(...args).catch(() => {
      // Erro já foi tratado no mutateAsync
    });
  }, [mutateAsync]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    mutate,
    mutateAsync,
    data,
    error,
    loading,
    reset,
  };
};

